import React from "react";
import { useParams, useNavigate, Navigate } from "react-router-dom";
import { Typography, Loading, ProgressBar } from "../../components/common";
import { ArrowLeft, Clock, CheckCircle } from "lucide-react";
import {
  useGetMasterCourseByIdQuery,
  useGetLevelByIdQuery,
} from "../../store/api/masterCourseApi";
import SuggestionBoardImage from "../../images/SuggestionBoardImage";
import LessonTimeline from "../../components/CourseComp/LessonTimeline";
import SoftLockModal from "../../components/lessons/SoftLockModal";
import { Feature, LessonTimeLine } from "../../types/newtypes";
import { formatTimeLeft } from "../../utils/progressUtils";
import {
  useGetCurrentUserQuery,
  useGetEntitlementsQuery,
} from "../../store/api/postgresUserApi";

const SOFT_LOCK_FALLBACK_MESSAGE =
  "The remaining content will be unlocked soon. Please check back later.";

const LevelDetail: React.FC = () => {
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: entitlements } = useGetEntitlementsQuery(undefined, {
    skip: !currentUser,
  });
  const showCertificateAction =
    entitlements?.features.includes(Feature.COURSE_CERTIFICATES) === true;
  const { masterCourseId, levelId } = useParams<{
    masterCourseId: string;
    levelId: string;
  }>();
  const navigate = useNavigate();
  const [showSoftLockModal, setShowSoftLockModal] = React.useState(false);
  const [softLockMessage, setSoftLockMessage] = React.useState<string | null>(
    null,
  );

  // Fetch Master Course to get Level metadata (v11 API)
  const { data: masterCourse, isLoading: isLoadingMasterCourse } =
    useGetMasterCourseByIdQuery(masterCourseId!, { skip: !masterCourseId });

  // Fetch Level with lessons (legacy /course-levels endpoint)
  const {
    data: levelWithLessons,
    isLoading: isLoadingLevel,
    error: levelError,
  } = useGetLevelByIdQuery(levelId!, { skip: !levelId });

  const isLoading = isLoadingMasterCourse || isLoadingLevel;

  // Find level metadata from master course
  const levelMetadata = masterCourse?.levels.find((l) => l.id === levelId);

  // Handle soft lock modal
  const openSoftLockModal = (message?: string | null) => {
    const trimmed = typeof message === "string" ? message.trim() : "";
    setSoftLockMessage(trimmed || SOFT_LOCK_FALLBACK_MESSAGE);
    setShowSoftLockModal(true);
  };

  const closeSoftLockModal = () => {
    setShowSoftLockModal(false);
  };

  const handleLessonClick = (_event: unknown, lesson: LessonTimeLine) => {
    if (levelWithLessons?.softLock) {
      const limit = levelWithLessons.softLock.afterLessonOrder;
      const lessonOrder = "order" in lesson ? lesson.order : undefined;
      if (
        typeof lessonOrder === "number" &&
        lessonOrder > limit &&
        lesson.status !== "completed"
      ) {
        openSoftLockModal(levelWithLessons.softLock.message);
        return;
      }
    }
    if (lesson.status === "unlocked" || lesson.status === "completed") {
      // Pass level context for trophy/celebration page on level completion
      navigate(`/lesson/${lesson.id}`, {
        state: {
          courseId: levelId,
          lessons: lessons.map((l) => ({
            id: l.id,
            order: l.order,
            status: l.status,
          })),
          // Level context for trophy page
          levelId,
          levelTitle: levelMetadata?.title,
          levelNumber: levelMetadata?.levelNumber,
          masterCourseId,
          masterCourseTitle: masterCourse?.title,
          // All levels for dynamic next level navigation
          allLevels: [...(masterCourse?.levels || [])]
            .sort((a, b) => a.levelNumber - b.levelNumber)
            .map((l) => ({
              id: l.id,
              levelNumber: l.levelNumber,
              title: l.title,
              status: l.status,
            })),
          showCertificateAction,
        },
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px] bg-background">
        <Loading />
      </div>
    );
  }

  // If level fetch failed but we have master course data, show error
  if (levelError || (!levelWithLessons && !isLoadingLevel)) {
    const errorData =
      levelError && "data" in levelError && typeof levelError.data === "object"
        ? (levelError.data as { error_code?: string })
        : null;
    const isRetired = errorData?.error_code === "course_retired";
    const isPlanLocked = errorData?.error_code === "level_not_in_plan";
    const planLockedHeading =
      currentUser?.is_trial_org === true
        ? "Not included in your trial"
        : "Not included in your plan";

    // Log the error for debugging
    // eslint-disable-next-line no-console
    console.error("Level fetch error:", { levelId, error: levelError });

    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Typography variant="h4" className="text-gray-600">
          {isRetired
            ? "This course has been retired"
            : isPlanLocked
              ? planLockedHeading
              : "Unable to load module content"}
        </Typography>
        <Typography variant="p" className="text-gray-500">
          {isRetired
            ? "Your completed progress is preserved. Any certificates remain available in Certificates; eligible certified credentials remain in your Passport."
            : isPlanLocked
              ? `${planLockedHeading}.`
              : "Please try again later or contact support."}
        </Typography>
        <button
          onClick={() => navigate(`/master-courses/${masterCourseId}`)}
          className="text-primary hover:underline"
        >
          Return to Course
        </button>
      </div>
    );
  }

  if (!levelWithLessons || !levelMetadata) {
    return <Navigate to="/courseNotFound" replace />;
  }

  const totalMinutes = levelMetadata.estimatedDurationMinutes || 0;
  const timeDisplay = formatTimeLeft(
    levelMetadata.progressPercentage,
    totalMinutes,
    levelMetadata.isCompleted,
  );

  const lessons = [...(levelWithLessons.lessons || [])].sort(
    (a, b) => a.order - b.order,
  );

  return (
    <div className="flex flex-col gap-6">
      {/* Back button */}
      <button
        onClick={() => navigate(`/master-courses/${masterCourseId}`)}
        className="flex items-center gap-2 text-gray-600 hover:text-primary transition w-fit"
      >
        <ArrowLeft size={20} />
        <span className="text-sm font-medium">
          Back to {masterCourse?.title || "Course"}
        </span>
      </button>

      {/* Header section */}
      <div className="bg-white rounded-2xl shadow-sm p-6 border border-slate-200">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Icon */}
          <div className="w-[180px] h-[150px] flex-shrink-0 overflow-hidden rounded-lg">
            {levelMetadata.iconUri ? (
              <img
                src={levelMetadata.iconUri}
                alt={levelMetadata.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-100">
                <SuggestionBoardImage />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Typography
                variant="small"
                weight="bold"
                color="none"
                className="text-textPrimary-grey uppercase"
              >
                Module {levelMetadata.levelNumber}
              </Typography>
              {levelMetadata.isCompleted && (
                <div className="flex items-center gap-1 text-green-600 bg-green-100 px-2 py-1 rounded-full text-sm">
                  <CheckCircle size={14} />
                  <span>Completed</span>
                </div>
              )}
            </div>

            <Typography variant="h2" weight="bold" className="text-gray-900">
              {levelMetadata.title}
            </Typography>

            {/* Stats row */}
            <div className="flex flex-wrap items-center gap-6 mt-2">
              {totalMinutes > 0 && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={18} />
                  <span className="text-sm">
                    {totalMinutes > 60
                      ? `${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`
                      : `${totalMinutes}m`}
                  </span>
                </div>
              )}
              {!levelMetadata.isCompleted &&
                totalMinutes > 0 &&
                levelMetadata.progressPercentage > 0 && (
                <div className="flex items-center gap-2 text-gray-500">
                  <span className="text-sm">{timeDisplay}</span>
                </div>
              )}
              {(!totalMinutes || totalMinutes === 0) && (
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock size={18} />
                  <span className="text-sm">{timeDisplay}</span>
                </div>
              )}
            </div>

            {/* Overall progress */}
            <div className="mt-2 max-w-md">
              <ProgressBar progress={levelMetadata.progressPercentage} />
            </div>
          </div>
        </div>
      </div>

      {/* Units section */}
      <div className="flex flex-col gap-4">
        <Typography variant="h4" weight="bold" className="text-gray-900">
          {lessons.length > 0 ? `Units 1-${lessons.length}` : "Units"}
        </Typography>

        {lessons.length > 0 ? (
          <LessonTimeline
            lessons={lessons}
            onLessonClick={handleLessonClick}
            courseId={levelId || ""}
            completedLessons={
              lessons.filter((l) => l.status === "completed").length
            }
            showCertificateAction={showCertificateAction}
          />
        ) : (
          <div className="text-center py-8 text-gray-500">
            <Typography variant="p">
              No units available for this module.
            </Typography>
          </div>
        )}
      </div>

      {/* Soft Lock Modal */}
      <SoftLockModal
        open={showSoftLockModal}
        onClose={closeSoftLockModal}
        message={softLockMessage || SOFT_LOCK_FALLBACK_MESSAGE}
      />
    </div>
  );
};

export default LevelDetail;
